/* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent */
import { useCallback, useEffect, useRef } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { WhatsappTemplate } from '@/shared/types/broadcast';
import { EmailTemplate, SmsTemplate } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';

import { useCampaignSenders } from './useCampaignSenders';
import { DraftCampaignSender } from '../redux/actions';
import {
  CampaignForm,
  DEFAULT_NO_RESPONSE_WAIT_MINUTES,
  readVoiceCallSettings,
  transformDraftSendersToFormData,
} from '../utils';

/**
 * Hook to initialize the campaign build form with data from react-query
 * This replaces the Redux-based approach for sender initialization
 */
export const useCampaignBuildFormInit = (
  campaignId: string | undefined,
  methods: UseFormReturn<CampaignForm>,
  setFormKey: (key: (prev: number) => number) => void
) => {
  // Track which campaign we've initialized to prevent re-initialization
  const initializedCampaignIdRef = useRef<string | undefined>(undefined);

  const { campaign_senders, listStatus } = useCampaignSenders(campaignId);

  useEffect(() => {
    // Only initialize once per campaign when data is ready
    // This prevents re-initialization during save (when cache updates multiple times)
    // When user navigates away and back, the component remounts, resetting the ref
    const shouldInitialize =
      campaign_senders &&
      campaign_senders.length > 0 &&
      listStatus === 'success' &&
      campaignId &&
      initializedCampaignIdRef.current !== campaignId;

    if (!shouldInitialize) {
      return;
    }

    // Convert campaign senders to draft format
    let draftSenders: DraftCampaignSender[] = [];

    // Identify the primary sender based on which one has transitions
    let primarySenderIndex = 0;
    if (campaign_senders.length > 1) {
      const senderWithTransitions = campaign_senders.findIndex(
        (sender) => sender.transitions && sender.transitions.length > 0
      );
      if (senderWithTransitions !== -1) {
        primarySenderIndex = senderWithTransitions;
      }
    }

    draftSenders = campaign_senders.map((sender, index) => {
      const senderType = index === primarySenderIndex ? 'primary' : 'fallback';
      const template = sender.template as unknown as WhatsappTemplate;

      // Extract parameters from template components
      const headerParameters: Array<{ name: string }> = [];
      const bodyParameters: Array<{ name: string }> = [];
      const footerParameters: Array<{ name: string }> = [];
      let headerMediaParam: { type: 'id' | 'link'; value: string } | undefined;

      if (template?.components && Array.isArray(template.components)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        template.components.forEach((component: any) => {
          if (component.parameters && Array.isArray(component.parameters)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component.parameters.forEach((param: any) => {
              // Handle media headers (IMAGE, VIDEO, DOCUMENT)
              if (
                component.type === 'HEADER' &&
                (param.type === 'image' || param.type === 'video' || param.type === 'document')
              ) {
                const mediaData = param[param.type];
                if (mediaData) {
                  headerMediaParam = {
                    type: mediaData.id ? 'id' : 'link',
                    value: mediaData.id || mediaData.link || '',
                  };
                }
              }
              // Handle text parameters
              else if (param.text !== undefined) {
                const paramValue = param.text || '';

                switch (component.type) {
                  case 'HEADER':
                    headerParameters.push({ name: paramValue });
                    break;
                  case 'BODY':
                    bodyParameters.push({ name: paramValue });
                    break;
                  case 'FOOTER':
                    footerParameters.push({ name: paramValue });
                    break;
                }
              }
            });
          }
        });
      }

      const integrationType = (sender.integration_type as string) || IntegrationType.WHATSAPP;

      // Extract content based on integration type
      let content = '';
      let subject = '';
      if (integrationType === IntegrationType.WHATSAPP) {
        content = '';
      } else if (integrationType === IntegrationType.EMAIL) {
        content = (template as unknown as EmailTemplate)?.body || '';
        subject = (template as unknown as EmailTemplate)?.subject || '';
      } else {
        content = (template as unknown as SmsTemplate)?.text || '';
      }

      // For fallback senders, extract wait time from the transition pointing to this sender.
      // With chained no-response senders, the transition may come from ANY sender (not just primary).
      // Example: Primary → A → B means A's transition points to B, not primary's.
      let waitTimeMinutes: number | undefined;
      if (senderType === 'fallback') {
        // Search ALL senders for a transition pointing to this sender
        for (const potentialSource of campaign_senders) {
          if (potentialSource.transitions) {
            const transitionToThisSender = potentialSource.transitions.find(
              (t) => t.sender_id === sender.sender_id
            );
            if (transitionToThisSender) {
              waitTimeMinutes = transitionToThisSender.after_minutes;
              break; // Found it, stop searching
            }
          }
        }
      }

      return {
        senderId: sender.sender_id, // Include sender_id for matching transitions
        integrationId: sender.integration_id,
        integrationType,
        content,
        subject,
        template,
        headerParameters,
        bodyParameters,
        footerParameters,
        headerMediaParam,
        enabled: senderType === 'primary' ? true : sender.metadata?.enabled || false,
        transitions: sender.transitions || [],
        ...(waitTimeMinutes !== undefined && { waitTimeMinutes }),
        // The API also stores an explicit 'continue'; the form models the
        // default as null so it never counts as a customized value.
        amdOnMachine:
          (sender.metadata?.amd_on_machine as string) === 'continue'
            ? null
            : sender.metadata?.amd_on_machine,
      };
    });
    // Transform draft senders to form data
    const draftFormData = transformDraftSendersToFormData(draftSenders);
    if (draftFormData.primarySender) {
      // Deep clone the entire primary sender object to ensure complete mutability
      const primarySenderCopy = JSON.parse(
        JSON.stringify({
          integrationId: draftFormData.primarySender.integrationId || '',
          integrationType: draftFormData.primarySender.integrationType,
          content: draftFormData.primarySender.content || '',
          subject: draftFormData.primarySender.subject || '',
          template: draftFormData.primarySender.template,
          headerParameters: draftFormData.primarySender.headerParameters || [],
          bodyParameters: draftFormData.primarySender.bodyParameters || [],
          footerParameters: draftFormData.primarySender.footerParameters || [],
          headerMediaParam: draftFormData.primarySender.headerMediaParam,
          amdOnMachine: draftFormData.primarySender.amdOnMachine,
        })
      );

      // Set the entire primarySender object at once instead of individual properties
      methods.setValue('primarySender', primarySenderCopy);
    }

    if (draftFormData.fallbackIfNotDelivered !== undefined) {
      methods.setValue('fallbackIfNotDelivered', draftFormData.fallbackIfNotDelivered);
    }

    if (draftFormData.fallbackIfNoResponse !== undefined) {
      methods.setValue('fallbackIfNoResponse', draftFormData.fallbackIfNoResponse);
    }

    if (draftFormData.fallbackSenders && draftFormData.fallbackSenders.length > 0) {
      // Create deep copies of fallback senders to avoid read-only property errors
      // Use .map() but keep array structure (including undefined entries) to maintain correct indices
      const fallbackSendersCopy = draftFormData.fallbackSenders.map((sender) => {
        // If sender is undefined/null, return a base empty sender
        return {
          integrationId: sender.integrationId || '',
          integrationType: sender.integrationType,
          content: sender.content || '',
          subject: sender.subject || '',
          template: sender.template,
          waitTimeMinutes: sender.waitTimeMinutes || DEFAULT_NO_RESPONSE_WAIT_MINUTES,
          headerParameters: sender.headerParameters || [],
          bodyParameters: sender.bodyParameters || [],
          footerParameters: sender.footerParameters || [],
          headerMediaParam: sender.headerMediaParam,
          amdOnMachine: sender.amdOnMachine,
        };
      });
      methods.setValue('fallbackSenders', fallbackSendersCopy);
    }

    // Keys absent from the metadata stay undefined in the form, so saving again
    // does not materialize a platform default
    methods.setValue('voiceSettings', readVoiceCallSettings(campaign_senders));

    // Mark this campaign as initialized
    initializedCampaignIdRef.current = campaignId;

    // Force form to re-render by changing the key
    setFormKey((prev) => prev + 1);
  }, [campaign_senders, listStatus, methods, setFormKey, campaignId]);

  /**
   * Reset the initialization state.
   * Call this after saving senders to allow re-initialization on next data change.
   */
  const resetInitialization = useCallback(() => {
    initializedCampaignIdRef.current = undefined;
  }, []);

  return {
    listStatus,
    formInitialized: initializedCampaignIdRef.current === campaignId,
    resetInitialization,
  };
};
